import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type CreateAxiosDefaults,
  type InternalAxiosRequestConfig,
} from "axios"
import qs from "qs"

let httpInstance: AxiosInstance | undefined
let basePathPrefix: string = ""

export function createHttpClient(config: CreateAxiosDefaults): AxiosInstance {
  const instance = axios.create(config)
  return instance
}

type MaybePromise<T> = T | Promise<T>

const BE_HTTP_AUTH_MODE = {
  AUTHORIZATION_BEARER: "authorization_bearer",
  ACCESS_TOKEN_HEADER: "access_token_header",
} as const

type BeHttpAuthMode = (typeof BE_HTTP_AUTH_MODE)[keyof typeof BE_HTTP_AUTH_MODE]

const BODY_ACCESS_TOKEN_BEHAVIOR = {
  KEEP: "keep",
  STRIP: "strip",
  SET: "set",
  SET_IF_MISSING: "set_if_missing",
} as const

type BodyAccessTokenBehavior =
  (typeof BODY_ACCESS_TOKEN_BEHAVIOR)[keyof typeof BODY_ACCESS_TOKEN_BEHAVIOR]

interface BeHttpAuthConfig {
  mode: BeHttpAuthMode
  headerName?: string
}

interface CreateBeHttpClientParams<
  TAdditionals extends Record<string, unknown>,
> {
  baseURL: string
  timeoutMs: number
  auth: BeHttpAuthConfig
  getToken: () => MaybePromise<string | null>
  getLocale: () => string | undefined
  getAdditionals: (config: InternalAxiosRequestConfig) => TAdditionals
  bodyAccessTokenBehavior?: BodyAccessTokenBehavior
}

function normalizeToken(token: unknown): string | null {
  if (typeof token !== "string") return null
  const trimmed = token.trim()
  if (!trimmed) return null
  if (trimmed === "null" || trimmed === "undefined") return null
  return trimmed
}

function getContentType(headers: unknown): string | undefined {
  const h = (headers ?? {}) as Record<string, unknown>
  const value = (h["Content-Type"] ?? h["content-type"]) as unknown
  if (typeof value === "string") return value
  return undefined
}

function isFormUrlEncoded(headers: unknown) {
  const contentType = getContentType(headers)
  return !!contentType
    ?.toLowerCase()
    .includes("application/x-www-form-urlencoded")
}

function buildAdditionalsSnapshot<
  TAdditionals extends Record<string, unknown>,
>({
  additionals,
  appVersionOverride,
  accessToken,
  bodyAccessTokenBehavior,
}: {
  additionals: TAdditionals
  appVersionOverride: unknown
  accessToken: string | null
  bodyAccessTokenBehavior: BodyAccessTokenBehavior
}): Record<string, unknown> {
  const snapshot: Record<string, unknown> = { ...additionals }

  if (typeof appVersionOverride === "string") {
    const clientInfo = snapshot.client_info
    if (clientInfo && typeof clientInfo === "object") {
      snapshot.client_info = {
        ...(clientInfo as Record<string, unknown>),
        app_version: appVersionOverride,
      }
    }
  }

  if (accessToken) {
    if (bodyAccessTokenBehavior === BODY_ACCESS_TOKEN_BEHAVIOR.SET) {
      snapshot.access_token = accessToken
    }
    if (
      bodyAccessTokenBehavior === BODY_ACCESS_TOKEN_BEHAVIOR.SET_IF_MISSING &&
      (snapshot.access_token === undefined || snapshot.access_token === null)
    ) {
      snapshot.access_token = accessToken
    }
  }

  return snapshot
}

export function createBeHttpClient<
  TAdditionals extends Record<string, unknown>,
>({
  baseURL,
  timeoutMs,
  auth,
  getToken,
  getLocale,
  getAdditionals,
  bodyAccessTokenBehavior = BODY_ACCESS_TOKEN_BEHAVIOR.KEEP,
}: CreateBeHttpClientParams<TAdditionals>): AxiosInstance {
  const instance = axios.create({
    baseURL,
    timeout: timeoutMs,
  })

  instance.defaults.headers.post["content-type"] = "application/json"

  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const rawToken = await getToken()
      const accessToken = normalizeToken(rawToken)

      // Normalize headers into a plain object (AxiosHeaders is also compatible with this usage)
      const headers: Record<string, unknown> = {
        ...((config.headers as unknown as Record<string, unknown>) ?? {}),
      }

      if (auth.mode === BE_HTTP_AUTH_MODE.AUTHORIZATION_BEARER) {
        if (accessToken) {
          headers.Authorization = `Bearer ${accessToken}`
        } else {
          delete headers.Authorization
        }
      }

      if (auth.mode === BE_HTTP_AUTH_MODE.ACCESS_TOKEN_HEADER) {
        const headerName = auth.headerName || "access_token"
        if (accessToken) {
          headers[headerName] = accessToken
        } else {
          delete headers[headerName]
        }
      }

      config.headers = headers as any

      const locale = getLocale()
      config.params = {
        ...(config.params as Record<string, unknown>),
        ...(locale ? { locale } : {}),
      }

      const additionals = getAdditionals(config)
      const urlEncoded = isFormUrlEncoded(config.headers)

      if (urlEncoded) {
        const oldData =
          typeof config.data === "string"
            ? (qs.parse(config.data) as Record<string, unknown>)
            : ((config.data as Record<string, unknown>) ?? {})

        const snapshot = buildAdditionalsSnapshot({
          additionals,
          appVersionOverride: oldData?.app_version,
          accessToken,
          bodyAccessTokenBehavior,
        })

        const merged = {
          ...snapshot,
          ...oldData,
        }

        if (bodyAccessTokenBehavior === BODY_ACCESS_TOKEN_BEHAVIOR.STRIP) {
          delete merged.access_token
        }

        config.data = qs.stringify(merged)
        return config
      }

      const oldData = (config.data as Record<string, unknown> | undefined) ?? {}

      const snapshot = buildAdditionalsSnapshot({
        additionals,
        appVersionOverride: oldData?.app_version,
        accessToken,
        bodyAccessTokenBehavior,
      })

      const merged = {
        ...snapshot,
        ...oldData,
      }

      if (bodyAccessTokenBehavior === BODY_ACCESS_TOKEN_BEHAVIOR.STRIP) {
        delete merged.access_token
      }

      config.data = merged
      return config
    }
  )

  return instance
}

export function registerHttp(instance: AxiosInstance) {
  httpInstance = instance
}

export function getHttp(): AxiosInstance {
  if (!httpInstance) throw new Error("HTTP client not registered")
  return httpInstance
}

export function setBasePathPrefix(prefix: string) {
  basePathPrefix = prefix.endsWith("/") ? prefix : `${prefix}/`
}

export function withPrefix(path: string): string {
  if (!path) return basePathPrefix
  if (path.startsWith("/")) path = path.slice(1)
  return `${basePathPrefix}${path}`
}

export async function httpGet<T = any>(
  path: string,
  config?: AxiosRequestConfig
) {
  const http = getHttp()
  return http.get<T>(withPrefix(path), config)
}

export async function httpPost<T = any>(
  path: string,
  data?: any,
  config?: AxiosRequestConfig,
  overridePath?: string
) {
  const http = getHttp()
  return http.post<T>(overridePath || withPrefix(path), data, config)
}
